import { HistoryMessageItem, IMForwarding, MessageIncomingEvent, MessageOutgoingEvent } from '@wui/im'



import { useStoreContacts } from '@/entities/contact'
import { useNotInHistoryMessagesStore } from '@/entities/not-in-history-messages'
import { useStoreRooms } from '@/entities/room'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { useLocalization } from '@/shared/composables/localization'
import { getUnknownContactLabel } from '@/shared/utils/contacts'
import { resolveGuestDisplayName } from '@/shared/utils/guest'
import { isGuest } from '@/shared/utils/uri'

import { chatModel } from '../../chat'

const findOriginalForwardedEvent = (eventId: number) => {
  const fromChat = chatModel.useChatMessageStore().findById(eventId) as
    | HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
    | undefined

  if (fromChat) {
    return fromChat
  }

  return useNotInHistoryMessagesStore().getMessageByEventId(eventId) as
    | HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
    | undefined
}

export const getForwardingAuthorName = (
  event: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
): string => {
  const { t } = useLocalization()
  const { currentUser } = useCurrentUser()
  const { findByPServed } = useStoreContacts()
  const { currentGuest } = useGuestAuth()

  const executor = event.item.executor
  const contact = findByPServed(executor)

  if (contact?.name) {
    return contact.name
  }

  if (isGuest(executor)) {
    const guest = currentGuest.value
    return resolveGuestDisplayName(
      event.item.pServed,
      executor,
      guest ? { selfGuest: { pServed: guest.pServed, name: guest.login }} : undefined,
    ) ?? t('common.guest')
  }

  if (event.item.type === 'OutgoingMessageEvent' || currentUser.value?.imLogin === executor) {
    return currentUser.value?.name ?? t('common.externalContact')
  }

  if (executor) {
    return getUnknownContactLabel(executor)
  }

  return t('common.externalContact')
}

export const resolveForwardingAuthorName = (forwarding: IMForwarding): string => {
  const { t } = useLocalization()
  const storedName = forwarding.forwardingAuthorName?.trim()
  if (storedName) {
    return storedName
  }

  if (forwarding.forwardingEventId) {
    const originalEvent = findOriginalForwardedEvent(forwarding.forwardingEventId)
    if (originalEvent) {
      return getForwardingAuthorName(originalEvent)
    }
  }

  if (forwarding.forwardingChatPServed) {
    const roomGuests = useStoreRooms().findByPServed(forwarding.forwardingChatPServed)?.guests
    if (roomGuests?.size === 1) {
      const [onlyGuest] = roomGuests.values()
      if (onlyGuest?.name) {
        return resolveGuestDisplayName(forwarding.forwardingChatPServed, onlyGuest.pServed)
          ?? `${onlyGuest.name} (${t('common.guest')})`
      }
    }
  }

  return t('common.guest')
}

export const resolveForwardingAuthorContact = (forwarding: IMForwarding) => {
  if (!forwarding.forwardingEventId) {
    return undefined
  }
  const originalEvent = findOriginalForwardedEvent(forwarding.forwardingEventId)
  if (!originalEvent) {
    return undefined
  }
  return useStoreContacts().findByPServed(originalEvent.item.executor)
}
